import AppBar from '@mui/material/AppBar';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { lazy, Suspense, useState } from 'react';

import { Logo } from '@/components/atoms';
import { activeSocials as socials, PhoneIcon, socialIcons } from '@/components/molecules';
import { primaryNav } from '@/config/navigation';
import { useHeaderFit } from '@/hooks/useHeaderFit';
import { callablePhones } from '@/utils/phoneChannels';

// Fetched when the menu is first opened; most visits never open it.
const MobileNavDrawer = lazy(() => import('./MobileNavDrawer'));
import { HeaderActions } from './HeaderActions';
import { HeaderNav } from './HeaderNav';
import { ImportPromiseRibbon } from './ImportPromiseRibbon';

/** Height of the utility strip above the main bar; SiteLayout's spacer adds it. */
export const TOP_BAR_HEIGHT = 36;

/** The lines a visitor may ring; the Japan desk takes messages, not calls. */
const topBarPhones = callablePhones;

type Props = {
  /** Float over a dark hero until the visitor scrolls (§15). */
  transparent?: boolean;
};

export function SiteHeader({ transparent = false }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** Stays true after the first open, so the panel is fetched only once. */
  const [drawerEverOpened, setDrawerEverOpened] = useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 80 });
  /**
   * What the top row can actually hold at this width, measured rather than
   * assumed. Owned here, not in the two components it governs: the nav and
   * the control cluster are the two ends of one row, and a row cannot be
   * planned twice.
   */
  const { rowRef, visibleCount, showSecondaryCta } = useHeaderFit(primaryNav.length);
  const solid = !transparent || scrolled;
  const ink = solid ? 'text.primary' : '#FFFFFF';
  const muted = solid ? 'text.secondary' : 'rgba(255,255,255,0.8)';

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          color: ink,
          // Frosted glass once solid: the page shows through, blurred.
          bgcolor: solid
            ? 'rgba(var(--mui-palette-background-paperChannel) / 0.85)'
            : 'transparent',
          backdropFilter: solid ? 'saturate(180%) blur(12px)' : 'none',
          borderBottom: 1,
          borderColor: solid ? 'divider' : 'transparent',
          backgroundImage: 'none',
          transition: (t) =>
            t.transitions.create(['background-color', 'border-color', 'color'], {
              duration: t.transitions.duration.short,
            }),
        }}
      >
        {/* The promise, above everything and on every page. Unlike the strip
            below it this row never folds away: the whole point is that a
            visitor who has scrolled past the hero can still see what Kabex
            does. Its height feeds `SiteLayout`'s spacer through a CSS
            variable, so dismissing it moves the page up with it.

            It is also handed `scrolled`, so the strip's contents move up into
            it as the strip below folds away — the numbers to the ribbon's
            left, the social links to its right, each in one place or the
            other, never in both and never in neither. That handover is an
            md-and-up arrangement: the strip below is md-only, so below it the
            ribbon carries the links permanently on a row of its own, and the
            numbers not at all — a phone already has the drawer's call button,
            and the 32px row has no width to spare for eleven digits.

            `solid` is the same value the bar below reads, so the two surfaces
            are never in disagreement about whether the page has moved: over
            the home hero the ribbon is unpainted chrome like the nav itself,
            and from the first scroll it is the brand bar every other page
            shows from the start. */}
        <ImportPromiseRibbon showUtilities={scrolled} solid={solid} />

        {/* Utility strip: tablet and up, folds away once the visitor scrolls. */}
        <Collapse in={!scrolled} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              height: TOP_BAR_HEIGHT,
              px: { md: 8, lg: 12 },
              borderBottom: 1,
              borderColor: solid ? 'divider' : 'rgba(255,255,255,0.16)',
              typography: 'caption',
              color: muted,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={8}>
              <PhoneIcon sx={{ fontSize: 16 }} />
              {topBarPhones.map((p) => (
                <Link
                  key={p.e164}
                  href={`tel:${p.e164}`}
                  color="inherit"
                  underline="hover"
                  sx={{ fontWeight: 600 }}
                >
                  {p.display}
                </Link>
              ))}
            </Stack>
            {socials.length > 0 && (
              <Stack direction="row" spacing={2}>
                {socials.map((s) => {
                  const Icon = socialIcons[s.platform];
                  return (
                    <IconButton
                      key={s.platform}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Kabex Motors on ${s.label}`}
                      size="small"
                      // 28px, not the theme's 44px touch target: the strip is 36px tall
                      // and only shown from md up, where a pointer is the norm.
                      sx={{
                        minWidth: 0,
                        minHeight: 0,
                        color: 'inherit',
                        '&:hover': { color: solid ? 'primary.main' : ink },
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                    </IconButton>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Collapse>

        {/* The main bar, and the one row of the header whose contents have to
            be negotiated rather than laid out. `useHeaderFit` measures it, so
            the three children below have to stay its only three children and
            the nav has to stay the middle one — the hook reads the others'
            widths off this element to work out what the nav may spend. */}
        <Toolbar ref={rowRef} sx={{ gap: 4, minHeight: { xs: 64, lg: 72 }, px: { lg: 5, xl: 12 } }}>
          <Logo height={48} priority />

          <HeaderNav items={primaryNav} visibleCount={visibleCount} ink={ink} solid={solid} />

          {/* The controls, in `HeaderActions`. It is the end of the bar that
              keeps growing — two calls to action now, after the consultation
              page gave the chrome a second destination (§32) — while the four
              rows above and around it do not, so it answers for itself. */}
          <HeaderActions
            ink={ink}
            solid={solid}
            showSecondaryCta={showSecondaryCta}
            onOpenMenu={() => {
              setDrawerEverOpened(true);
              setDrawerOpen(true);
            }}
          />
        </Toolbar>
      </AppBar>
      {drawerEverOpened && (
        <Suspense fallback={null}>
          <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
